import React from 'react'
import PropTypes from 'prop-types'
import { Box } from '@mui/material'
import Icon from 'components/design-system/Icon'

const SIZES = {
  default: { box: 27, icon: 16, p: 'var(--space-8)' }, // icon 16 (chuẩn)
  medium: { box: 33, icon: 16, p: 'var(--space-8)' }, // icon 16 trong nút cao 33 (vd. toolbar AOI panel, cạnh Search Bar)
  large: { box: 33, icon: 24, p: 'var(--space-4)' } // icon 24 trong nút 33px (vd. back, refresh)
}

// Nền + viền theo state (Figma 8:1766)
const VARIANTS = {
  outline: {
    base: { bgcolor: 'var(--color-bg-control)', border: 'var(--stroke-default) solid var(--color-border-default)' },
    hover: { borderColor: 'var(--color-border-hover)', filter: 'drop-shadow(0 0 2px var(--color-effect-glow))' },
    active: { bgcolor: 'var(--color-bg-selected)', borderColor: 'var(--color-border-focus)', filter: 'none' },
    disabled: { bgcolor: 'var(--color-bg-raised)', borderColor: 'var(--color-border-subtle)' }
  },
  ghost: {
    base: { bgcolor: 'transparent', border: 'var(--stroke-default) solid transparent' },
    hover: { bgcolor: 'var(--color-bg-hover)' },
    active: { bgcolor: 'var(--color-bg-selected)' },
    disabled: { bgcolor: 'transparent' }
  }
}

// Viền tiến trình (state Downloading): chạy theo chu vi nút, bắt đầu giữa cạnh trên, theo chiều kim đồng hồ.
// Stroke 2px nằm trong viền 1px → tâm stroke cách mép 1px, bo góc 8 - 1 = 7.
const progressPath = (b) =>
  `M ${b / 2} 1 H ${b - 8} A 7 7 0 0 1 ${b - 1} 8 V ${b - 8} A 7 7 0 0 1 ${b - 8} ${b - 1} H 8 A 7 7 0 0 1 1 ${b - 8} V 8 A 7 7 0 0 1 8 1 Z`

/**
 * Icon Button (Figma 8:1766). `variant`: outline | ghost; `size`: default (27px, icon 16) | medium (33px, icon 16) | large (33px, icon 24).
 * Màu icon theo state: Default → Strong khi hover → Accent khi `active` → Disabled; nền / viền đổi theo state (xem VARIANTS).
 * `progress` (0–100) = state Downloading: viền accent chạy quanh nút theo % (ghost cũng hiện viền mặc định).
 */
const IconButton = React.forwardRef(
  ({ icon, variant = 'outline', size = 'default', active = false, disabled = false, progress, sx, ...props }, ref) => {
    const s = SIZES[size]
    const v = VARIANTS[variant]
    const downloading = typeof progress === 'number'
    const pct = downloading ? Math.min(100, Math.max(0, progress)) : 0

    return (
      <Box
        ref={ref}
        component="button"
        type="button"
        disabled={disabled}
        aria-pressed={active || undefined}
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
            width: s.box,
            height: s.box,
            position: 'relative',
            p: s.p,
            m: 0,
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-icon-default)',
            cursor: 'pointer',
            ...v.base,
            '&:hover:not(:disabled)': { color: 'var(--color-icon-strong)', ...v.hover },
            '&:focus-visible': { outline: '2px solid var(--color-border-focus)', outlineOffset: '2px' },
            '&:disabled': { color: 'var(--color-icon-disabled)', cursor: 'not-allowed', ...v.disabled }
          },
          active && {
            color: 'var(--color-icon-accent)',
            ...v.active,
            '&:hover:not(:disabled)': { color: 'var(--color-icon-accent)', ...v.active }
          },
          downloading && { borderColor: 'var(--color-border-default)' },
          ...(Array.isArray(sx) ? sx : [sx])
        ]}
        {...props}
      >
        <Icon name={icon} size={s.icon} />
        {downloading && (
          <Box
            component="svg"
            aria-hidden
            width={s.box}
            height={s.box}
            viewBox={`0 0 ${s.box} ${s.box}`}
            fill="none"
            sx={{ position: 'absolute', top: -1, left: -1, pointerEvents: 'none', overflow: 'visible' }}
          >
            {pct > 0 && (
              <path
                d={progressPath(s.box)}
                pathLength={100}
                strokeDasharray={`${pct} 100`}
                stroke="var(--color-border-focus)"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transition: 'stroke-dasharray 0.2s' }}
              />
            )}
          </Box>
        )}
      </Box>
    )
  }
)

IconButton.displayName = 'IconButton'

IconButton.propTypes = {
  icon: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['outline', 'ghost']),
  size: PropTypes.oneOf(['default', 'medium', 'large']),
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  progress: PropTypes.number,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

export default IconButton
